/**
 * I18n Service Implementation
 * Handles language detection, translation lookup, and persistence
 * Loads translations from JSON files in locales/ directory
 * @module @infra/i18n
 */

import type {
  Language,
  I18nService,
  Namespace,
  TranslationKey,
  TranslationParams,
  TranslationResource,
} from './i18n.types';
import {
  isLanguageSupported,
  getLanguageLocale,
  normalizeLanguageTag,
  getAvailableLanguages as listSupportedLanguages,
} from './i18n.languages';
import { loadNamespaceTranslations, getNestedValue, interpolateParams } from './i18n.loader';
import { ensureLanguageWebfont } from './i18n.webfont';
import { I18N_DEFAULT_LANGUAGE, I18N_LANGUAGE_KEY } from './i18n.constants';

const STORAGE_KEY = I18N_LANGUAGE_KEY;

type TranslationRecord = TranslationResource;

/**
 * I18n Service implementation
 * Loads JSON translations on-demand
 */
class I18nServiceImpl implements I18nService {
  private currentLanguage: Language = I18N_DEFAULT_LANGUAGE;
  private translations: Record<Language, TranslationRecord> = {
    en: {},
    vi: {},
    ms: {},
    zh: {},
    km: {},
    my: {},
  };
  /** null = unrestricted. Set from the entitlement answer; see setAllowedLanguages. */
  private allowedLanguages: Set<string> | null = null;
  private loadedLanguages = new Set<Language>();
  private loadedNamespaces = new Map<Language, Set<Namespace>>();
  private namespaceLoadPromises = new Map<Language, Map<Namespace, Promise<void>>>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  // `auth` is in the base set (not lazy) so the always-mounted SessionRevokedGuard
  // modal — which can open on a WS/refresh event without any prior route navigation
  // to the auth pages — never flashes raw keys like `auth:sessionRevoked.title`.
  private readonly baseNamespaces: Namespace[] = ['common', 'landing', 'errors', 'auth', 'emailVerification'];

  constructor() {
    this.detectLanguage();
  }

  /**
   * Adopts a `locale` query param (sent by the FusionAuth pages), saves it and strips
   * it from the URL so a later in-app language switch is not overridden on reload.
   */
  private consumeLocaleParam(): Language | undefined {
    if (typeof window === 'undefined') return undefined;
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('locale');
    if (raw === null) return undefined;

    url.searchParams.delete('locale');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

    const language = normalizeLanguageTag(raw);
    if (!language) return undefined;
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // Ignore storage write errors (e.g. privacy mode).
    }
    return language;
  }

  /**
   * Detect language from multiple sources
   * Priority: `locale` URL param → localStorage → navigator → `I18N_DEFAULT_LANGUAGE`
   */
  private detectLanguage(): void {
    const fromUrl = this.consumeLocaleParam();
    if (fromUrl) {
      this.currentLanguage = fromUrl;
      return;
    }

    // A stored preference wins over everything below: it is the reader's own answer,
    // and an entitlement that later disallows it moves them off it explicitly rather
    // than by being silently re-detected here.
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        // Resolve rather than match exactly: a preference stored under an older code
        // (`zh-hk`) must still land on the language the user chose. Matching exactly
        // would drop them to the default language instead.
        const resolved = stored ? normalizeLanguageTag(stored) : undefined;
        if (resolved) {
          this.currentLanguage = resolved;
          // Rewrite the stored value so the next boot needs no resolution, and so the
          // rest of the app never reads an obsolete code back out of storage.
          if (resolved !== stored) {
            try {
              localStorage.setItem(STORAGE_KEY, resolved);
            } catch {
              // Ignore storage write errors (e.g. privacy mode).
            }
          }
          return;
        }
      } catch {
        // Ignore storage access errors (e.g. privacy mode) and fall through.
      }
    }

    if (typeof navigator !== 'undefined' && navigator.language) {
      // Resolve the whole tag rather than truncating at the first hyphen: a Hong Kong
      // browser reports `zh-HK`/`zh-Hant-HK`, and every intermediate form gets its turn
      // at the alias table on the way down.
      const navLang = normalizeLanguageTag(navigator.language);
      if (navLang) {
        this.currentLanguage = navLang;
        return;
      }
    }

    this.currentLanguage = I18N_DEFAULT_LANGUAGE;
  }

  /**
   * Initialize service and load translations
   * Public method for manual initialization
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        // Apply the document language and its webfont before awaiting anything, so a
        // reload into a non-Latin language paints with the right `lang` and face
        // rather than only correcting on the next explicit language switch.
        this.updateHTMLAttributes(this.currentLanguage);

        // Load base namespaces for current language
        await this.ensureNamespacesLoaded(this.currentLanguage, this.baseNamespaces);

        // Optionally preload default language for fallback behavior
        if (this.currentLanguage !== I18N_DEFAULT_LANGUAGE) {
          await this.ensureNamespacesLoaded(I18N_DEFAULT_LANGUAGE, this.baseNamespaces);
        }

        this.initialized = true;
        this.initPromise = null;
      } catch (error) {
        console.error('Failed to load translations:', error);
        this.initPromise = null;
        throw error;
      }
    })();

    return this.initPromise;
  }

  async preloadNamespaces(namespaces: Namespace[]): Promise<void> {
    await this.ensureNamespacesLoaded(this.currentLanguage, namespaces);
  }

  /**
   * Are ALL the given namespaces already loaded for the current language? Lets callers
   * derive readiness synchronously (during render) instead of going through a
   * setState(false)→preload→setState(true) cycle that flashes a loader on cached routes.
   * Empty array → true (a route with no extra namespaces is always ready).
   */
  hasNamespacesLoaded(namespaces: Namespace[]): boolean {
    return namespaces.every((ns) => this.isNamespaceLoaded(this.currentLanguage, ns));
  }

  private async ensureNamespacesLoaded(language: Language, namespaces: Namespace[]): Promise<void> {
    const languageNamespaces = this.loadedNamespaces.get(language) ?? new Set<Namespace>();
    this.loadedNamespaces.set(language, languageNamespaces);

    const loadMap = this.namespaceLoadPromises.get(language) ?? new Map<Namespace, Promise<void>>();
    this.namespaceLoadPromises.set(language, loadMap);

    const missingNamespaces = namespaces.filter((ns) => !languageNamespaces.has(ns));
    if (!missingNamespaces.length) {
      return;
    }

    const loadTasks = missingNamespaces.map((ns) => {
      const existingPromise = loadMap.get(ns);
      if (existingPromise) {
        return existingPromise;
      }

      const loadPromise = (async () => {
        try {
          const translations = await loadNamespaceTranslations(language, [ns]);
          this.translations = {
            ...this.translations,
            [language]: {
              ...this.translations[language],
              ...translations,
            },
          };
          // Only mark loaded on success — a failed load must stay "missing" so it retries.
          languageNamespaces.add(ns);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('i18n:namespaceLoaded', { detail: { language, namespace: ns } })
            );
          }
        } finally {
          // Always clear the in-flight entry so a FAILED load stays retryable — a cached
          // rejected promise would otherwise make every retry return the same failure.
          loadMap.delete(ns);
        }
      })();

      loadMap.set(ns, loadPromise);
      return loadPromise;
    });

    await Promise.all(loadTasks);
    this.loadedLanguages.add(language);
  }

  /**
   * Translate a key with optional parameters
   * Synchronous version - returns key if not yet initialized
   * @example t('auth.login.title') → "Sign In"
   * @example t('errors.validation.minLength', { min: 8 }) → "Must be at least 8 characters"
   */
  t(key: TranslationKey, params?: TranslationParams): string {
    if (!this.initialized) {
      return params?.defaultValue ?? key;
    }

    const namespace = this.getNamespaceFromKey(key);
    if (namespace && !this.isNamespaceLoaded(this.currentLanguage, namespace)) {
      // Fire-and-forget namespace preload; UI will re-render on namespaceLoaded event.
      // .catch: the loader now propagates network failures — swallow here so a transient
      // failure isn't an unhandled rejection (a later render / retry re-attempts).
      void this.ensureNamespacesLoaded(this.currentLanguage, [namespace]).catch(() => {});
      return params?.defaultValue ?? key;
    }

    const normalizedKey = key.replace(':', '.');
    const langTranslations = this.translations[this.currentLanguage];
    let value = getNestedValue(langTranslations, normalizedKey);

    // Fallback to the default language when the active one has no copy for this key.
    //
    // The default language only preloads the base namespaces, so a lazy namespace
    // missing from the active language found nothing here and rendered its raw key.
    // Pull the namespace in on demand — same fire-and-forget shape as above, and the
    // `namespaceLoaded` event re-renders with the fallback copy.
    if (!value && this.currentLanguage !== I18N_DEFAULT_LANGUAGE) {
      if (namespace && !this.isNamespaceLoaded(I18N_DEFAULT_LANGUAGE, namespace)) {
        void this.ensureNamespacesLoaded(I18N_DEFAULT_LANGUAGE, [namespace]).catch(() => {});
      } else if (this.loadedLanguages.has(I18N_DEFAULT_LANGUAGE)) {
        value = getNestedValue(this.translations[I18N_DEFAULT_LANGUAGE], normalizedKey);
      }
    }

    if (!value) {
      console.warn(`Translation not found for key: ${key}`);
      return params?.defaultValue ?? key;
    }

    return interpolateParams(value, params as Record<string, string | number> | undefined);
  }

  /**
   * Async translation method
   * Waits for initialization if needed
   */
  async tAsync(key: TranslationKey, params?: TranslationParams): Promise<string> {
    try {
      await this.initialize();
      const namespace = this.getNamespaceFromKey(key);
      if (namespace) {
        await this.ensureNamespacesLoaded(this.currentLanguage, [namespace]);
      }
    } catch {
      console.warn('i18n not initialized, returning key:', key);
      return params?.defaultValue ?? key;
    }

    const normalizedKey = key.replace(':', '.');
    const langTranslations = this.translations[this.currentLanguage];
    let value = getNestedValue(langTranslations, normalizedKey);

    // Fallback to default language if not found
    if (
      !value &&
      this.currentLanguage !== I18N_DEFAULT_LANGUAGE &&
      this.loadedLanguages.has(I18N_DEFAULT_LANGUAGE)
    ) {
      const defaultTranslations = this.translations[I18N_DEFAULT_LANGUAGE];
      value = getNestedValue(defaultTranslations, normalizedKey);
    }

    // Return key if still not found
    if (!value) {
      console.warn(`Translation not found for key: ${key}`);
      return params?.defaultValue ?? key;
    }

    // Interpolate parameters
    return interpolateParams(value, params as Record<string, string | number> | undefined);
  }

  /**
   * Get current language
   */
  getLanguage(): Language {
    return this.currentLanguage;
  }

  /**
   * Set current language and persist
   */
  async setLanguage(language: Language): Promise<void> {
    if (!isLanguageSupported(language)) {
      throw new Error(`Language '${language}' is not supported`);
    }

    await this.ensureNamespacesLoaded(language, this.baseNamespaces);

    if (language !== I18N_DEFAULT_LANGUAGE) {
      await this.ensureNamespacesLoaded(I18N_DEFAULT_LANGUAGE, this.baseNamespaces);
    }

    this.currentLanguage = language;

    // Persist to localStorage
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, language);
      } catch (error) {
        console.warn('Failed to persist language to localStorage:', error);
      }
    }

    // Update HTML attributes
    this.updateHTMLAttributes(language);

    // Emit change event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('i18n:languageChange', { detail: { language } }));
    }
  }

  /**
   * Update HTML lang attribute
   */
  private updateHTMLAttributes(language: Language): void {
    ensureLanguageWebfont(language);
    if (typeof document !== 'undefined') {
      // The BCP-47 tag rather than the internal code — for most languages it carries a
      // region the code does not (`en` → `en-US`). Screen readers announce from `lang`,
      // and browsers consult it when choosing a face for scripts a font stack leaves
      // ambiguous. Which face actually gets used is not left to that though: the font
      // stacks in `index.css` name `Noto Sans HK` / `Khmer` / `Myanmar` outright, and
      // `ensureLanguageWebfont` fetches them.
      document.documentElement.lang = getLanguageLocale(language);
    }
  }

  /**
   * Get available languages
   * Data-driven from LANGUAGE_INFO (i18n.languages.ts) — the switcher UI lists
   * whatever languages are configured there, so adding one needs no UI change.
   *
   * Narrowed by `setAllowedLanguages` once the entitlement answer arrives. Filtering
   * HERE rather than in each picker means the four places that render a language list
   * need no knowledge of entitlements, and a caller that never sets a restriction
   * (the pre-login landing page) keeps the full set.
   */
  getAvailableLanguages() {
    const all = listSupportedLanguages();
    if (!this.allowedLanguages) return all;
    const narrowed = all.filter((info) => this.allowedLanguages?.has(info.code));
    // Fails OPEN: an allowlist naming nothing this build ships would otherwise leave
    // the user with no language at all, and the UI language gates no server call —
    // offering one too many costs nothing, offering none strands the reader.
    return narrowed.length > 0 ? narrowed : all;
  }

  setAllowedLanguages(codes: readonly string[] | null): void {
    const next = codes ? new Set(codes) : null;
    const same =
      (next === null && this.allowedLanguages === null) ||
      (next !== null &&
        this.allowedLanguages !== null &&
        next.size === this.allowedLanguages.size &&
        [...next].every((c) => this.allowedLanguages?.has(c)));
    if (same) return;
    this.allowedLanguages = next;
    if (typeof window !== 'undefined') {
      // `useLanguage` recomputes the list on render, so it needs telling — the answer
      // lands after first paint, long after the pickers have already rendered.
      window.dispatchEvent(new CustomEvent('i18n:allowedLanguagesChange'));
    }
  }

  /**
   * Check if language is supported
   */
  isLanguageSupported(language: string): language is Language {
    return isLanguageSupported(language);
  }

  private getNamespaceFromKey(key: string): Namespace | null {
    if (key.includes(':')) {
      return key.split(':')[0] as Namespace;
    }

    if (key.includes('.')) {
      return key.split('.')[0] as Namespace;
    }

    return null;
  }

  private isNamespaceLoaded(language: Language, namespace: Namespace): boolean {
    return this.loadedNamespaces.get(language)?.has(namespace) ?? false;
  }
}

/**
 * Singleton instance
 */
let instance: I18nServiceImpl | null = null;

/**
 * Get or create I18n service instance
 */
export function getI18nService(): I18nService {
  if (!instance) {
    instance = new I18nServiceImpl();
  }
  return instance;
}

export { I18nServiceImpl };
